import React from 'react';
import { Tabs } from '@workday/canvas-kit-react/tabs';
import { autoId, ID_BACK_BUTTON } from '@workday/upc-common';
import { ProfileImageSettingsRow } from './personal/profile-image/ProfileImageSettingsRow';
import { ProfileNameSettingsRow } from './personal/profile-name/ProfileNameSettingsRow';
import { ContactInfoSettingsRow } from './personal/contact-info-settings-row';
import { DeleteAccountSettingsRow } from './security/confirmDelete/DeleteAccountSettingsRow';
import { ChangePasswordSettingsRow } from './security/changePassword/ChangePasswordSettingsRow';
import { AuthenticatorSettingsRow } from './security/authenticator/AuthenticatorSettingsRow';
import { SessionDetailSettingsRow } from './security/sessionDetails/SessionDetailSettingsRow';
import { TertiaryButton } from '@workday/canvas-kit-react/button';
import { arrowLeftIcon } from '@workday/canvas-system-icons-web';
import { useHistory } from 'react-router-dom';
import { HOME_PATH } from '../../react-router-paths';
import styles from './AccountSettingsContainer.module.scss';
import { ID_SETTINGS_PAGE_TITLE } from '../../autoIds';
import { EmailSettingsRow } from './personal/email-settings/email-settings-row';
import { UserLocalePreferencesRow } from './preferences/locale-preferences/locale-preferences-row';
import { useVerifyUriMutation } from '../../slices/account-api.slice';
import { environment } from '../../../environments/environment';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '../../hooks';

export function AccountSettingsContainer({ userInfo }) {
    const history = useHistory();
    const queryParams = useQueryParams();

    const { t } = useTranslation('account');

    const [verifyUri] = useVerifyUriMutation();

    const onBackClick = async () => {
        const redirectUri = queryParams.get('redirectUri');
        const clientId = queryParams.get('clientId');
        if (redirectUri && clientId) {
            // @ts-ignore
            const { data: isValidResponse } = await verifyUri({ redirectUri, clientId });
            if (isValidResponse?.isValid ?? false) {
                // Have to cast as Window to ensure location be set to a url
                (window as Window).location.replace(redirectUri);
            } else {
                history.replace(HOME_PATH);
            }
        } else {
            history.replace(HOME_PATH);
        }
    };

    return (
        <>
            <TertiaryButton
                {...autoId(ID_BACK_BUTTON)}
                icon={arrowLeftIcon}
                onClick={onBackClick}
                className={styles.backButton}>
                {t('AccountSettingsContainer.backButton')}
            </TertiaryButton>
            <h1 {...autoId(ID_SETTINGS_PAGE_TITLE)} className='pageTitle'>
                {t('AccountSettingsContainer.title')}
            </h1>
            <Tabs>
                <Tabs.List>
                    <Tabs.Item data-id='first'>{t('AccountSettingsContainer.tabItemPersonalInfo')}</Tabs.Item>
                    <Tabs.Item data-id='second'>{t('AccountSettingsContainer.tabItemSecuritySettings')}</Tabs.Item>
                    <Tabs.Item data-id='third'>{t('AccountSettingsContainer.tabItemPreferences')}</Tabs.Item>
                </Tabs.List>
                <Tabs.Panel data-id='first'>
                    <ProfileImageSettingsRow />
                    <ProfileNameSettingsRow userInfo={userInfo} />
                    <ContactInfoSettingsRow userInfo={userInfo} />
                </Tabs.Panel>
                <Tabs.Panel data-id='second'>
                    <ChangePasswordSettingsRow />
                    <AuthenticatorSettingsRow />
                    {environment.disableGdpr ? null : <DeleteAccountSettingsRow />}
                    <SessionDetailSettingsRow />
                </Tabs.Panel>
                <Tabs.Panel data-id='third'>
                    <EmailSettingsRow />
                    <UserLocalePreferencesRow userInfo={userInfo} />
                </Tabs.Panel>
            </Tabs>
        </>
    );
}
